import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { FormControl } from '@angular/forms';

import {
  DialogService,
  DialogStatus,
} from 'src/app/core/services/dialog.service';
import { AppDatePickerInputMaxType } from 'src/app/core/types/app-date-picker-input-max-type.type';
import { AppDatePickerInputMinType } from 'src/app/core/types/app-date-picker-input-min-type.type';
import { AppTimePickerInputMaxType } from 'src/app/core/types/app-time-picker-input-max-type.type';
import { AppTimePickerInputMinType } from 'src/app/core/types/app-time-picker-input-min-type.type';
import { DateTimeService } from '../../../core/services/date-time.service';

@Component({
    selector: 'app-periodic-date-time-picker',
    templateUrl: './periodic-date-time-picker.component.html',
    standalone: false
})
export class PeriodicDateTimeComponent implements OnInit {
  //#region properties
  #dialogService = inject(DialogService);

  @Input() config: PeriodicDateTimeConfig;

  @Output('startDateTime') output_startDateTimeValue = new EventEmitter<any>();
  @Output('endDateTime') output_endDateTimeValue = new EventEmitter<any>();

  startDate;
  endDate;
  startTime;
  endTime;

  startDateTime;
  endDateTime;

  maxStartTime;
  minEndTime;

  isShowError: boolean = false; 

  //#endregion

  constructor() {}

  ngOnInit() {
    this.initValues();
  }

  ngOnChanges(): void {
    this.initValues();
    if (this.config.minStartTimeTheCurrentTimeIfDateIsToday == undefined)
      this.config.minStartTimeTheCurrentTimeIfDateIsToday = true;
  }

  initValues() {
    if (!this.config.isAdvancedSearch) this.startDate = 'today';

    if (this.config?.startDateTimeValue || this.config?.endDateTimeValue)
      this.separateDateAndTime();
  }

  checkMinMaxTime() {
    if (!this.config.isAdvancedSearch) {
      //hide error
      if (this.startTime && this.endTime) this.isShowError = false;

      let endDate = new Date(this.endDate);
      endDate.setHours(0);
      endDate.setMinutes(0);
      endDate.setSeconds(0);

      if (
        this.startDate == String(this.endDate) ||
        this.startDate == String(endDate)
      ) {
        this.minEndTime = this.startTime;
        this.maxStartTime = this.endTime;

        //check error
        if (this.startTime && this.endTime && this.startTime > this.endTime) {
          this.isShowError = true;
          this.startTime = '';
          this.endTime = '';
        }
      } else {
        this.minEndTime = '';
        this.maxStartTime = '';
      }
    }
  }

  separateDateAndTime() {
    // get DateTime Start
    if (this.config?.startDateTimeValue)
      [this.startDate, this.startTime] = DateTimeService.separateDateTimeUTC(
        this.config?.startDateTimeValue
      );

    // get DateTime End
    if (this.config?.endDateTimeValue)
      [this.endDate, this.endTime] = DateTimeService.separateDateTimeUTC(
        this.config?.endDateTimeValue
      );
  }

  combineDateAndTime() {
    this.setMinTimeType();

    //start dateTime
    if (this.startDate && this.startTime)
      this.startDateTime = DateTimeService.combineDateTimeToUTC(
        this.startDate?.toString().toUTC(),
        this.startTime
      );
    else if (
      !this.config.isAdvancedSearch ||
      (!this.startDate && !this.startTime)
    )
      this.startDateTime = null;
    else if (!this.startTime && this.startDate)
      this.startDateTime = DateTimeService.combineDateTimeToUTC(
        this.startDate?.toString().toUTC(),
        '00:00'
      );
    else if (!this.startDate && this.startTime)
      this.startDateTime = DateTimeService.combineDateTimeToUTC(
        DateTimeService.getTodayDate().toUTC(),
        this.startTime
      );

    //end dateTime
    if (this.endDate && this.endTime)
      this.endDateTime = DateTimeService.combineDateTimeToUTC(
        this.endDate?.toString().toUTC(),
        this.endTime
      );
    else if (!this.config.isAdvancedSearch || (!this.endDate && !this.endTime))
      this.endDateTime = null;
    else if (!this.endTime && this.endDate)
      this.endDateTime = DateTimeService.combineDateTimeToUTC(
        this.endDate?.toString().toUTC(),
        '23:59'
      );
    else if (!this.endDate && this.endTime)
      this.endDateTime = DateTimeService.combineDateTimeToUTC(
        DateTimeService.getTodayDate().toUTC(),
        this.endTime
      );

    if (this.config.isConvertToIsoString) {
      //start dateTime
      let _startSendDate = new Date(this.startDate?.toString());
      let _startSendTime = this.startTime.toString();
      _startSendDate.setHours(
        _startSendTime.split(':')[0],
        _startSendTime.split(':')[1]
      );
      this.startDateTime = _startSendDate?.toISOString();
      //end dateTime
      let _endSendDate = new Date(this.endDate?.toString());
      let _endSendTime = this.endTime.toString();
      _endSendDate.setHours(
        _endSendTime.split(':')[0],
        _endSendTime.split(':')[1]
      );
      this.endDateTime = _endSendDate?.toISOString();
    }

    this.emitStartDateTime();
    this.emitEndDateTime();
  }

  setMinTimeType() {
    if (this.config?.isEditMode) return;
    if (!this.config.isAdvancedSearch) {
      const startDate = new Date(this.startDate).toDateString();
      const endDate = new Date(this.endDate).toDateString();
      const currentDate = new Date().toDateString();
      const currentTime = DateTimeService.getTime();
      //start time
      if (startDate == currentDate) {
        if (this.config.maxStartTimeTheCurrentTimeIfDateIsToday) {
          this.config.maxStartTimeType = 'now';
          if (currentTime < this.startTime || !this.startTime)
            this.startTime = DateTimeService.getTime();
        }
        if (this.config.minStartTimeTheCurrentTimeIfDateIsToday) {
          this.config.minStartTimeType = 'now';
          if (currentTime > this.startTime || !this.startTime)
            this.startTime = DateTimeService.getTime();
        }
      } else {
        if (this.config.maxStartTimeTheCurrentTimeIfDateIsToday)
          this.config.maxStartTimeType = 'custom';
        if (this.config.minStartTimeTheCurrentTimeIfDateIsToday)
          this.config.minStartTimeType = 'custom';
      }

      // end time
      if (endDate == currentDate) {
        if (this.config.maxEndTimeTheCurrentTimeIfDateIsToday) {
          this.config.maxEndTimeType = 'now';
          if (currentTime < this.endTime || !this.endTime)
            this.endTime = DateTimeService.getTime();
        }
      } else if (this.config.maxEndTimeTheCurrentTimeIfDateIsToday)
        this.config.maxEndTimeType = 'custom';
    }
  }

  showErrorDialog(event: any) {
    this.#dialogService.openDialog({
      title: 'common.error',
      message: event?.errorMessage,
      status: DialogStatus.error,
    });
  }

  emitStartDateTime() {
    this.output_startDateTimeValue.emit(this.startDateTime);
  }

  emitEndDateTime() {
    this.output_endDateTimeValue.emit(this.endDateTime);
  }
}

export interface PeriodicDateTimeConfig {
  minStartDateType?: AppDatePickerInputMinType;
  maxStartDateType?: AppDatePickerInputMaxType;
  minStartTimeType?: AppTimePickerInputMinType;
  maxStartTimeType?: AppTimePickerInputMaxType;
  minEndDateType?: AppDatePickerInputMinType;
  maxEndDateType?: AppDatePickerInputMaxType;
  minEndTimeType?: AppTimePickerInputMinType;
  maxEndTimeType?: AppTimePickerInputMaxType;
  maxStartTimeTheCurrentTimeIfDateIsToday?: boolean;
  minStartTimeTheCurrentTimeIfDateIsToday?: boolean;
  maxEndTimeTheCurrentTimeIfDateIsToday?: boolean;
  isEditMode?: boolean;
  startDateLabel?: string;
  endDateLabel?: string;
  startTimeLabel?: string;
  endTimeLabel?: string;
  startDateFormControl?: FormControl;
  endDateFormControl?: FormControl;
  startTimeFormControl?: FormControl;
  endTimeFormControl?: FormControl;
  responsiveClassName?: string;
  startDateTimeValue?: any;
  endDateTimeValue?: any;
  isConvertToIsoString?: boolean;
  isAdvancedSearch?: boolean;
  isShowStartDateIconValidation?: boolean;
  isShowEndDateIconValidation?: boolean;
  isHideClearBtn?: boolean;
}
